import React from 'react';
import { Link } from 'react-router-dom';
import { CheckIcon, ChevronRightIcon, MapPinIcon, XIcon } from 'lucide-react';
import type { ChoirEvent, Rsvp } from '../../types';
import { Badge } from '../ui/Badge';
import { eventTitle, eventTypeClass, eventTypeShort, formatDate, formatRange, parseDate, weekdayShort } from '../../utils/labels';
interface EventRowProps {
  event: ChoirEvent;
  rsvp?: Rsvp;
  to: string;
  expected?: boolean;
  onPridem: () => void;
  onNeMorem: () => void;
}

/** Vrstica dogodka s hitro potrditvijo udeležbe — brez odpiranja podrobnosti. */
export function EventRow({
  event,
  rsvp,
  to,
  expected = true,
  onPridem,
  onNeMorem
}: EventRowProps) {
  const d = parseDate(event.datum);
  const pridem = rsvp?.status === 'pridem';
  const neMorem = rsvp?.status === 'ne_morem';
  return <div className={`rounded-xl border bg-white p-3.5 shadow-card transition-colors duration-150 ease-out ${rsvp ? 'border-line' : 'border-amber-200'}`} data-id="element-251" data-mp="3q4nv34bqwrp9">
      <div className="flex items-center gap-4" data-id="element-252" data-mp="2ycm32svp74vh">
        <div className="flex h-14 w-14 shrink-0 flex-col items-center justify-center rounded-lg bg-canvas" data-id="element-253" data-mp="2ybatgcfkn2wg">
          <span className="text-[11px] uppercase tracking-wide text-ink-muted" data-id="element-254" data-mp="2tby7rv0fs1vg">
            {weekdayShort(event.datum)}
          </span>
          <span className="text-lg font-semibold leading-5 text-ink" data-id="element-255" data-mp="333c6w1oh6zo2">{d.getDate()}.</span>
          <span className="text-[10px] text-ink-muted" data-id="element-256" data-mp="2fdzw4xdi2jjn">{d.getMonth() + 1}. mes.</span>
        </div>

        <Link to={to} className="min-w-0 flex-1" data-id="element-257" data-mp="2afgdp6y47pw1">
          <div className="flex flex-wrap items-center gap-1.5" data-id="element-258" data-mp="2ah15iot1ouq7">
            <Badge className={eventTypeClass[event.type]} data-id="element-259" data-mp="2of7tnd86zgco">{eventTypeShort[event.type]}</Badge>
            {event.potovanje?.aktivno && <Badge className="bg-purple-50 text-purple-700 border-purple-200" data-id="element-260" data-mp="2jejv86hmvx8f">Gostovanje</Badge>}
            {event.opomba && <Badge className="bg-canvas text-ink-muted border-line" data-id="element-261" data-mp="3uvjlkwkf81zt">{event.opomba}</Badge>}
            {!event.steje && <Badge className="bg-slate-100 text-ink-muted border-line" data-id="element-262" data-mp="2sgkf6lxzcgqk">Ne šteje</Badge>}
          </div>
          <p className="mt-1.5 truncate text-sm font-semibold text-ink" data-id="element-263" data-mp="2xfnh3xiny6ub">{eventTitle(event)}</p>
          <p className="mt-0.5 flex items-center gap-1 truncate text-[13px] text-ink-muted" data-id="element-264" data-mp="31jd80tmt90ii">
            {event.datumDo ? formatRange(event.datum, event.datumDo) : `${formatDate(event.datum)} · ${event.zacetek}${event.konec ? `–${event.konec}` : ''}`}
            <span className="mx-1 text-line" data-id="element-265" data-mp="2ds7s6egve887">|</span>
            <MapPinIcon className="h-3.5 w-3.5 shrink-0" data-id="element-266" data-mp="2dqmwglitkj8r" />
            <span className="truncate" data-id="element-267" data-mp="2iqrewh3afo1m">{event.lokacija}</span>
          </p>
        </Link>

        <Link to={to} aria-label={`Odpri ${eventTitle(event)}`} className="hidden shrink-0 rounded-md p-1.5 text-ink-muted transition-colors duration-150 ease-out hover:bg-canvas hover:text-ink sm:block" data-id="element-268" data-mp="2nisn9rxc4umd">
          <ChevronRightIcon className="h-4 w-4" data-id="element-269" data-mp="1v0f0qohg8v2l" />
        </Link>
      </div>

      {expected ? <div className="mt-3 flex items-center gap-2 border-t border-line pt-3" data-id="element-270" data-mp="1nhxintvic7y0">
          <button onClick={onPridem} aria-pressed={pridem} className={`flex flex-1 items-center justify-center gap-1.5 rounded-lg border px-3 py-2 text-[13px] font-semibold transition-colors duration-150 ease-out ${pridem ? 'border-green-500 bg-green-500 text-white' : 'border-line bg-white text-ink hover:bg-canvas'}`} data-id="element-271" data-mp="1ikxb1ej0ex4s">
            <CheckIcon className="h-4 w-4" data-id="element-272" data-mp="zr1oiq3kx0nl" />
            Pridem
          </button>
          <button onClick={onNeMorem} aria-pressed={neMorem} className={`flex flex-1 items-center justify-center gap-1.5 rounded-lg border px-3 py-2 text-[13px] font-semibold transition-colors duration-150 ease-out ${neMorem ? 'border-rose-500 bg-rose-500 text-white' : 'border-line bg-white text-ink hover:bg-canvas'}`} data-id="element-273" data-mp="uub6vyc4lx02">
            <XIcon className="h-4 w-4" data-id="element-274" data-mp="2ky3m0dx050ro" />
            Ne morem priti
          </button>
        </div> : <p className="mt-3 border-t border-line pt-3 text-[13px] text-ink-muted" data-id="element-275" data-mp="276ygwwav7g9y">
          Za vas ta dogodek ni obvezen.
        </p>}

      {neMorem && rsvp?.razlog && <p className="mt-2 text-[12px] text-ink-muted" data-id="element-276" data-mp="2c3gkvtydfal8">Razlog: {rsvp.razlog}</p>}
    </div>;
}